import _ from 'lodash'
import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { PageArea, PageProps } from '..'
import { projectPrivate } from '../../higher-order-components/projectPrivate'
import { useProjectPrivateContext } from '../../context/projectContext'
import { SearchConditionAndColumnState } from '../../../services/model/searchConditionAndColumnState'
import { WbsItemSearchConditionVO } from '../../../domain/value-object/WbsItemSearchConditionVo'
import {
  ColDef,
  ColumnApi,
  ColumnState,
  ColumnVisibleEvent,
  FilterChangedEvent,
  FirstDataRenderedEvent,
  GetContextMenuItemsParams,
  GridApi,
  GridOptions,
  GridReadyEvent,
  MenuItemDef,
  RowDataUpdatedEvent,
  SortChangedEvent,
} from 'ag-grid-community'
import { useTaskActualWorkProxyRegistrationCondition } from './hooks/useSearchCondition'
import { useTaskActualWorkProxyRegistrationData } from './hooks/useTaskActualWorkProxyRegistrationData'
import { TaskActualWorkProxyRegistrationConditionHeader } from './Header/TaskActualWorkProxyRegistrationConditionHeader'
import { ProxyProjectMemberSelector } from '../../components/headers/ProjectMemberSelector'
import {
  DisplayUnit,
  ShiftType,
} from '../../containers/BulkSheetView/components/header/datePicker/TermSelector'
import DateVO from '../../../vo/DateVO'
import {
  DateTerm,
  DISPLAY_DATE_SHORT_FORMAT_WITH_DAY,
} from '../../../utils/date'
import moment from 'moment'
import { BulkSheetView } from '../../containers/BulkSheetView'
import { useTaskActualWorkProxyRegistrationGridOptions } from './gridOptions'
import { refreshDynamicColumnDef } from './gridOptions/columnDefs/taskActualWorkProxyRegistrationGridOptions'
import { RouteComponentProps } from 'react-router'
import { FunctionLayer } from '../../../store/functionLayer'
import { SelectOptionWithIcon } from '../../components/select/model'
import {
  addCopyURLAndNameMenuItems,
  addCopyURLMenuItems,
  WbsItem,
} from '../../containers/BulkSheetView/gridOptions/contextMenu'
import { getSelectedNode } from '../../containers/BulkSheetView/lib/gridApi'
import { connect } from 'react-redux'
import store, { AllState } from '../../../store'
import SavePopper from '../../containers/BulkSheetView/components/header/SaveButtonArea'
import {
  addGlobalMessage,
  addScreenMessage,
  MessageLevel,
} from '../../../store/messages'
import { intl } from '../../../i18n'
import { KEY_SAVE } from '../../model/keyBind'
import { useKeyBind } from '../../hooks/useKeyBind'
import { useDialog } from './hooks/dialogState'
import { TaskActualWorkProxyRegistrationRow } from './taskActualWorkProxyRegistration'
import CancelConfirmDialog from '../../components/dialogs/CancelConfirmDialog'
import { SortedColumnState } from '../../model/bulkSheetColumnSortState'
import { useBulkSheetState } from '../../containers/BulkSheetView/hooks/bulkSheetState/bulkSheetState'
import { UiStateKey } from '../../../lib/commons/uiStates'
import { useSelectColumnOptions } from './hooks/selectColumnOptions'
import { useColumnSetting } from '../../containers/BulkSheetView/components/columnSelector/useColumnSetting'
import ColumnSettingPopper from '../../containers/BulkSheetView/components/columnSelector/ColumnSettingPopper'
import { ROW_HEIGHT } from '../../containers/BulkSheet'
import { usePageState } from '../../hooks/usePageState'
import { SavedUIState } from '../../components/dialogs/SavedUIStateDialog/SavedUIStateList'
import SavedUIStateDialog from '../../components/dialogs/SavedUIStateDialog'
import { TaskActualWorkProxyRegistrationHeader } from './Header/TaskActualWorkProxyRegistrationHeader'
import Loading from '../../components/process-state-notifications/Loading'
import { DecimalAndSexagesimalScale } from '../../../lib/functions/decimalAndSexagesimalScale'

type PathProps = {
  code?: string
  term?: string
}

type StateProps = {
  functionLayers: Immutable.Map<number, FunctionLayer>
  user
}

type TaskActualWorkProxyPageState = {
  dateFormat: string
  rowHeight: number
  displayUnit: DisplayUnit
  decimalAndSexagesimalScale: DecimalAndSexagesimalScale
}

type Props = PageProps & RouteComponentProps<PathProps> & StateProps

const TaskActualWorkProxyRegistration = projectPrivate((props: Props) => {
  const functionUuid = props.uuid
  const [selectedMember, setSelectedMember] = useState<
    SelectOptionWithIcon<string> | undefined
  >(undefined)
  const isMemberSelected = useMemo(() => !!selectedMember, [selectedMember])
  const [refreshAfterUpdateState, setRefreshAfterUpdateState] =
    useState<boolean>(false)
  const [loading, setLoading] = useState<boolean>(false)
  const columnApi = useRef<ColumnApi>()
  const gridApi = useRef<GridApi>()
  const gridOptionsRef = useRef<GridOptions>()
  const [filteredColumns, setFilteredColumns] = useState<ColDef[]>([])
  const [sortColumnsState, setSortColumnsState] = useState<SortedColumnState[]>(
    []
  )
  const dialog = useDialog()
  const [dateTerm, setDateTerm] = useState<DateTerm>()
  const { project } = useProjectPrivateContext()
  const bulkSheetViewRef = useRef<HTMLDivElement>(null)

  const defaultPageState = useMemo<TaskActualWorkProxyPageState>(
    () => ({
      dateFormat: DISPLAY_DATE_SHORT_FORMAT_WITH_DAY,
      rowHeight: ROW_HEIGHT.SMALL,
      displayUnit: DisplayUnit.WEEK,
      decimalAndSexagesimalScale: DecimalAndSexagesimalScale.DECIMAL,
    }),
    []
  )

  const {
    dateFormat,
    displayUnit,
    rowHeight,
    decimalAndSexagesimalScale,
    updatePageState,
  } = usePageState<TaskActualWorkProxyPageState>(functionUuid, defaultPageState)

  const { selectColumnOptions } = useSelectColumnOptions(project.uuid)
  const columnSetting = useColumnSetting()
  const gridOptions = useTaskActualWorkProxyRegistrationGridOptions({
    projectUuid: project.uuid,
    context: selectColumnOptions,
  })

  // Manage search condition state.
  const {
    searchCondition,
    updateSearchCondition,
    changeSelectedMember,
    overrideSearchCondition,
    resetSearchCondition,
  } = useTaskActualWorkProxyRegistrationCondition()

  // Manage row data, which is search with conditions.
  const { totalDataSize, hitDataSize, count, data, refresh, save } =
    useTaskActualWorkProxyRegistrationData({
      applicationFunctionUuid: functionUuid,
      projectUuid: project.uuid,
      searchCondition,
      selectedMemberUuid: selectedMember?.value,
    })

  const dataRef = useRef<TaskActualWorkProxyRegistrationRow[]>([])
  dataRef.current = data

  const submit = useRef<Function>()
  submit.current = save

  useEffect(() => {
    if (gridOptions) {
      gridOptions.context = {
        ...gridOptions.context,
        dateFormat,
      }
    }
    gridApi.current?.refreshCells({ force: true })
  }, [dateFormat, gridOptions])

  useEffect(
    () => {
      if (gridOptions && selectColumnOptions) {
        gridOptions.context = {
          ...gridOptions.context,
          ...selectColumnOptions,
          isSelectedProjectMember: selectedMember !== undefined,
        }
      }
    },
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [selectColumnOptions, selectedMember]
  )

  useEffect(() => {
    if (!selectedMember) return
    changeSelectedMember(selectedMember.value)
    setRefreshAfterUpdateState(true)
  }, [selectedMember, changeSelectedMember])

  const refreshAll = useCallback(async () => {
    setLoading(true)
    try {
      const refreshRows = refresh
      await refreshRows()
    } finally {
      setLoading(false)
      gridApi.current?.refreshCells({ force: true })
    }
  }, [refresh])

  useEffect(() => {
    if (!refreshAfterUpdateState) return
    refreshAll()
    setRefreshAfterUpdateState(false)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshAfterUpdateState])

  useEffect(() => {
    if (hitDataSize === 0) {
      setLoading(false)
    }
  }, [hitDataSize])

  useEffect(() => {
    if (hitDataSize === 0) {
      setLoading(false)
    }
  }, [hitDataSize])

  // This is used when applying permanent ui state.
  const overrideSearchConditionAndSearch = useCallback(
    (newSearchCondition: WbsItemSearchConditionVO) => {
      overrideSearchCondition(newSearchCondition)
      setRefreshAfterUpdateState(true)
    },
    [overrideSearchCondition]
  )

  const applyStoredState = useCallback(
    (storedState: SearchConditionAndColumnState) => {
      overrideSearchConditionAndSearch(storedState.searchCondition)
      columnApi.current?.applyColumnState({
        state: storedState.columnState,
      })
    },
    [overrideSearchConditionAndSearch]
  )

  const onCloseSearchItemLabel = useCallback(() => {
    setRefreshAfterUpdateState(true)
  }, [])

  const onSearch = useCallback(() => {
    setRefreshAfterUpdateState(true)
  }, [])

  const refreshDynamicColumn = useCallback(
    async (startDate, endDate) => {
      if (!startDate || !endDate) return
      await refreshDynamicColumnDef({
        api: gridOptions.api,
        dateTerm: {
          startDate: startDate.formatForApi(),
          endDate: endDate.formatForApi(),
        },
        decimalAndSexagesimalScale,
      })
      if (gridOptions.context) {
        gridOptions.context.isSelectedProjectMember =
          selectedMember !== undefined
      }
    },
    [
      gridOptions.api,
      gridOptions.context,
      selectedMember,
      decimalAndSexagesimalScale,
    ]
  )

  const getTermList = useCallback((startDate, endDate): string[] => {
    if (!startDate || !endDate) {
      return []
    }
    const startDateVO = new DateVO(startDate)
    const endDateVO = new DateVO(endDate)
    const diff =
      moment(endDateVO.toDate()).diff(moment(startDateVO.toDate()), 'days') + 1
    return Array.from({ length: diff }).map((_, index) =>
      moment(startDateVO.toDate()).add(index, 'day').format('YYYY-MM-DD')
    )
  }, [])

  const shiftDisplayTerm = useCallback(
    (shiftType: ShiftType, unit?: DisplayUnit) => {
      const dateList = getTermList(dateTerm?.startDate, dateTerm?.endDate)
      let startDate = new DateVO()
      let endDate = new DateVO()
      const oldStartDate = new DateVO(dateList[0])
      const displayTermUnit = unit ? unit : displayUnit
      if (!oldStartDate) shiftType = ShiftType.THIS_TERM
      switch (shiftType) {
        case ShiftType.THIS_TERM:
          const now = DateVO.now()
          if (displayTermUnit === DisplayUnit.WEEK) {
            startDate = now.getFirstDayOfWeek().addDays(1)
            endDate = startDate.getLastDayOfWeek().addDays(1)
          } else if (displayTermUnit === DisplayUnit.MONTH) {
            startDate = now.getFirstDayOfMonth()
            endDate = startDate.getLastDayOfMonth()
          }
          break
        case ShiftType.FORWARD:
          if (displayTermUnit === DisplayUnit.WEEK) {
            startDate = oldStartDate.addWeeks(1)
            endDate = startDate.getLastDayOfWeek().addDays(1)
          } else if (displayTermUnit === DisplayUnit.MONTH) {
            startDate = oldStartDate.addMonths(1)
            endDate = startDate.getLastDayOfMonth()
          }
          break
        case ShiftType.BACKWARD:
          if (displayTermUnit === DisplayUnit.WEEK) {
            startDate = oldStartDate.subtractWeeks(1)
            endDate = startDate.getLastDayOfWeek().addDays(1)
          } else if (displayTermUnit === DisplayUnit.MONTH) {
            startDate = oldStartDate.subtractMonths(1)
            endDate = startDate.getLastDayOfMonth()
          }
          break
      }
      refreshDynamicColumn(startDate, endDate)
      setDateTerm({
        startDate: startDate.formatForApi(),
        endDate: endDate.formatForApi(),
      })
    },
    [
      dateTerm?.endDate,
      dateTerm?.startDate,
      getTermList,
      displayUnit,
      refreshDynamicColumn,
    ]
  )

  const contextMenu = useCallback((params: GetContextMenuItemsParams) => {
    if (
      !params.node ||
      !params.node.data ||
      !params.node.data.body ||
      !params.node.data.body.wbsItem
    ) {
      return []
    }

    const selectedWbsItems: WbsItem[] = getSelectedNode(params.api).map(
      node => node.data.body!.wbsItem
    )
    const items: (string | MenuItemDef)[] = [
      addCopyURLMenuItems(selectedWbsItems),
      addCopyURLAndNameMenuItems(selectedWbsItems),
    ]
    return items
  }, [])

  const onSubmit = useCallback(async () => {
    setLoading(true)
    try {
      gridOptions.api?.stopEditing()
      if (gridOptions.context.errors?.hasMessage()) {
        // const data = getCurrentData()
        store.dispatch(
          addGlobalMessage({
            type: MessageLevel.WARN,
            title: intl.formatMessage({ id: 'global.warning.businessError' }),
            text: gridOptions.context.errors.toMessage(id => {
              return data.findIndex(d => d.uuid === id) + 1
            }),
          })
        )
        return
      }
      const response = await save(
        getTermList(dateTerm?.startDate, dateTerm?.endDate),
        selectedMember?.value
      )
      if (!response) return
      if (!response.hasError && !response.hasWarning) {
        store.dispatch(
          addScreenMessage(functionUuid, {
            type: MessageLevel.SUCCESS,
            title: intl.formatMessage({ id: 'registration.complete' }),
          })
        )
        await refreshAll()
      }
    } finally {
      setLoading(false)
    }
  }, [
    gridOptions.api,
    save,
    getTermList,
    dateTerm?.startDate,
    dateTerm?.endDate,
    selectedMember?.value,
    data,
    functionUuid,
    refreshAll,
  ])

  useKeyBind(
    [
      {
        key: KEY_SAVE,
        fn: onSubmit,
        stopDefaultBehavior: true,
      },
    ],
    [onSubmit]
  )

  const hasChanged = useCallback((): boolean => {
    return data.some(v => v.edited)
  }, [data])
  const onReload = useCallback(async () => {
    if (hasChanged()) {
      dialog.openCancelConfirm()
    } else {
      setLoading(true)
      await refresh()
      setLoading(false)
    }
  }, [dialog, hasChanged, refresh])

  const onCancelConfirm = useCallback(() => {
    dialog.closeCancelConfirm()
    refreshAll()
  }, [dialog, refreshAll])

  const bulkSheetState = useBulkSheetState(
    functionUuid,
    project.uuid,
    UiStateKey.TaskActualWorkProxyColumnAndFilterState
  )

  useEffect(() => {
    shiftDisplayTerm(ShiftType.THIS_TERM)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bulkSheetState.initialized])

  // BulkSheet action ---
  const onFilterChanged = useCallback(
    (e: FilterChangedEvent) => {
      const filterModel = e.api?.getFilterModel()
      delete filterModel['uuid']
      bulkSheetState.saveFilterState(filterModel)
      setFilteredColumns(
        Object.keys(filterModel)
          .map(col => e.api.getColumnDef(col))
          .filter(v => !!v && v.field !== 'uuid') as ColDef[]
      )
    },
    [bulkSheetState]
  )

  useEffect(() => {
    if (dateTerm?.startDate && dateTerm?.endDate) {
      refreshDynamicColumn(
        new DateVO(dateTerm.startDate),
        new DateVO(dateTerm.endDate)
      )
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [decimalAndSexagesimalScale])

  const rememberColumnState = useCallback(() => {
    const columnState = columnApi.current?.getColumnState()
    columnState && bulkSheetState.saveColumnState(columnState)
  }, [bulkSheetState])

  const onSortedColumnsStateChanged = useCallback((e: SortChangedEvent) => {
    const sortedColumns = e.columnApi
      .getColumnState()
      .filter(col => !!col.sort)
      .map(col => {
        return {
          ...e.api.getColumnDef(col.colId),
          colId: col.colId,
        }
      })
      .filter(v => !!v) as ColDef[]
    e.api.setSuppressRowDrag(0 < sortedColumns.length)

    const columnState = columnApi.current?.getColumnState()
    const sortedColumnState: { [colId: string]: ColumnState } = {}
    columnState &&
      columnState.forEach(state => {
        if (state.sort) {
          sortedColumnState[state.colId] = state
        }
      })
    const sortedColumnsState: SortedColumnState[] = sortedColumns.map(col => {
      return {
        colId: col.colId,
        field: col.field,
        headerName: col.headerName,
        sort: col.colId ? sortedColumnState[col.colId]?.sort : null,
      }
    })

    setSortColumnsState(sortedColumnsState)
  }, [])

  const onRowDataUpdated = useCallback(
    (e: RowDataUpdatedEvent<TaskActualWorkProxyRegistrationRow>) =>
      e.api?.refreshCells({
        columns: ['rowNumber'],
        force: true,
      }),
    []
  )
  const onSortChanged = useCallback(
    (e: SortChangedEvent) => {
      rememberColumnState()
      onSortedColumnsStateChanged(e)
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  const onDeleteFilteredColumn = useCallback(
    (column: ColDef) => {
      if (!column || !gridOptions.api) return
      const filterModel = gridOptions.api.getFilterModel()
      delete filterModel[column.colId || column.field || '']
      gridOptions.api.setFilterModel(filterModel)
    },
    [gridOptions.api]
  )

  const resetFilters = useCallback(() => {
    if (!gridOptions.api) return
    gridOptions.api.setFilterModel([])
  }, [gridOptions.api])

  const onDeleteSortedColumn = useCallback(
    (colId: string | ColDef<TaskActualWorkProxyRegistrationRow>) => {
      gridOptions.columnApi?.applyColumnState({
        state: [{ colId: colId.toString(), sort: null }],
      })
    },
    [gridOptions.columnApi]
  )

  const onDeleteSortedAllColumns = useCallback(() => {
    gridOptions.columnApi?.applyColumnState({
      defaultState: { sort: null },
    })
  }, [gridOptions.columnApi])

  const onChangeSortColumnState = useCallback(
    (
      colId: string | ColDef<TaskActualWorkProxyRegistrationRow>,
      sort: 'asc' | 'desc' | null
    ) => {
      gridOptions.columnApi?.applyColumnState({
        state: [{ colId: colId.toString(), sort }],
      })
    },
    [gridOptions.columnApi]
  )

  const onGridReady = useCallback(
    (event: GridReadyEvent<TaskActualWorkProxyRegistrationRow>) => {
      gridOptionsRef.current = gridOptions
      gridApi.current = event.api
      columnApi.current = event.columnApi
    },
    [gridOptions]
  )

  const onFirstDataRendered = useCallback(
    (event: FirstDataRenderedEvent<TaskActualWorkProxyRegistrationRow>) => {
      if (!_.isEmpty(bulkSheetState.filterState)) {
        gridOptions.api?.setFilterModel(bulkSheetState.filterState)
      }
      if (!_.isEmpty(bulkSheetState.columnState)) {
        gridOptions.columnApi?.applyColumnState({
          state: bulkSheetState.columnState,
          applyOrder: true,
        })
      }
      bulkSheetState.finishRestoring()
      setLoading(false)
    },
    [bulkSheetState, gridOptions]
  )

  const onResetColumn = useCallback(() => {
    gridOptions.columnApi?.resetColumnState()
  }, [gridOptions])

  const onChangeRowHeight = useCallback(
    (value: number) => {
      updatePageState({ rowHeight: value })
    },
    [updatePageState]
  )

  const onChangeDateFormat = useCallback(
    (value: string) => {
      updatePageState({ dateFormat: value })
    },
    [updatePageState]
  )

  const onChangeTerm = useCallback(
    (displayUnit: DisplayUnit) => {
      updatePageState({ displayUnit })
      shiftDisplayTerm(ShiftType.THIS_TERM, displayUnit)
    },
    [updatePageState, shiftDisplayTerm]
  )

  const onChangeDecimalAndSexagesimalScale = useCallback(
    (value: DecimalAndSexagesimalScale) => {
      updatePageState({ decimalAndSexagesimalScale: value })
    },
    [updatePageState]
  )

  const onColumnVisible = useCallback(
    (e: ColumnVisibleEvent) => {
      rememberColumnState()
      onSortedColumnsStateChanged(e)
    },
    [rememberColumnState, onSortedColumnsStateChanged]
  )

  useEffect(() => {
    gridOptions.api?.resetRowHeights()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rowHeight])

  return (
    <PageArea>
      {props.functionLayers.size === 1 && (
        <SavePopper loading={loading} onSubmit={onSubmit} />
      )}
      <Loading isLoading={loading} elem={bulkSheetViewRef.current} />
      <ProxyProjectMemberSelector
        initialMessage={intl.formatMessage({
          id: 'taskActualWork.proxy.registration.initial.message',
        })}
        message={intl.formatMessage({
          id: 'taskActualWork.proxy.registration.message',
        })}
        selectedMember={selectedMember}
        setSelectedMember={setSelectedMember}
        projectUuid={project.uuid}
        loginUser={props.user}
        height={bulkSheetViewRef.current?.offsetHeight}
        offset={20}
      />
      <TaskActualWorkProxyRegistrationConditionHeader
        hit={hitDataSize}
        count={count}
        total={totalDataSize}
        searchCondition={searchCondition}
        onChange={updateSearchCondition}
        search={onSearch}
        resetCondition={resetSearchCondition}
        functionUuid={functionUuid}
        applyStoredState={applyStoredState}
        onCloseSearchItemLabel={onCloseSearchItemLabel}
        isLoading={loading}
        isMemberSelected={isMemberSelected}
      />
      <TaskActualWorkProxyRegistrationHeader
        onReload={onReload}
        displayUnit={displayUnit}
        dateTerm={dateTerm}
        onChangeTerm={onChangeTerm}
        shiftDisplayTerm={shiftDisplayTerm}
        filteredColumns={filteredColumns}
        onDeleteFilteredColumn={onDeleteFilteredColumn}
        resetFilters={resetFilters}
        onDeleteSortedColumn={onDeleteSortedColumn}
        onDeleteSortedAllColumns={onDeleteSortedAllColumns}
        onChangeSortColumnState={onChangeSortColumnState}
        sortColumnsState={sortColumnsState}
        onClickColumnSettingButton={columnSetting.toggle}
        columnSettingOpen={columnSetting.isOpen}
        rowHeight={rowHeight}
        onChangeRowHeight={onChangeRowHeight}
        dateFormat={dateFormat}
        onChangeDateFormat={onChangeDateFormat}
        decimalAndSexagesimalScale={decimalAndSexagesimalScale}
        onChangeDecimalAndSexagesimalScale={onChangeDecimalAndSexagesimalScale}
      />
      <BulkSheetView
        gridOptions={gridOptions}
        ref={bulkSheetViewRef}
        rowData={data}
        rowHeight={rowHeight}
        getContextMenuItems={contextMenu}
        onColumnResized={rememberColumnState}
        onColumnMoved={rememberColumnState}
        onColumnVisible={onColumnVisible}
        onFilterChanged={onFilterChanged}
        onFirstDataRendered={onFirstDataRendered}
        onGridReady={onGridReady}
        onSortChanged={onSortChanged}
        onRowDataUpdated={onRowDataUpdated}
      />
      <ColumnSettingPopper
        anchorEl={columnSetting.anchorEl}
        open={columnSetting.isOpen}
        close={columnSetting.close}
        columnApi={gridOptions.columnApi ?? undefined}
        gridApi={gridOptions.api ?? undefined}
        height={bulkSheetViewRef.current?.offsetHeight}
        openSavedUiStateDialog={dialog.openUiState}
        initializeColumnState={onResetColumn}
        applicationFunctionUuid={functionUuid}
        uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
        columnState={bulkSheetState.columnState}
        offset={120}
      />
      {dialog.uiState && (
        <SavedUIStateDialog
          applicationFunctionUuid={functionUuid}
          open={true}
          title={intl.formatMessage({
            id: 'savedUIState.BULK_SHEET_UI_STATE_COLUMN_AND_FILTER',
          })}
          uiStateKey={UiStateKey.BulkSheetUIStateColumnAndFilter}
          sharable={false}
          currentUIState={{
            columnState: bulkSheetState.columnState,
            filterState: bulkSheetState.filterState,
          }}
          onSelect={(uiState: SavedUIState) => {
            const { columnState, filterState } = uiState.UIState
            if (columnState) {
              bulkSheetState.saveColumnState(columnState)
              gridOptions.columnApi?.applyColumnState({
                state: columnState,
                applyOrder: true,
              })
            }
            if (filterState) {
              bulkSheetState.saveFilterState(filterState)
              gridOptions.api?.setFilterModel(filterState)
            }
            dialog.closeUiState()
          }}
          onClose={dialog.closeUiState}
        />
      )}
      <CancelConfirmDialog
        open={dialog.cancelConfirm}
        onConfirm={onCancelConfirm}
        onClose={dialog.closeCancelConfirm}
      />
    </PageArea>
  )
})

const mapStateToProps = (state: AllState) => ({
  functionLayers: state.functionLayer.layers,
  user: state.user.user,
})

export default connect(mapStateToProps)(TaskActualWorkProxyRegistration)
